(() => {
  const bannerSelector = '.flex-banner[data-fx-track="banner"]';
  const headerSelector = [
    '.contour-header-section',
    '.section-header.shopify-section-group-header-group',
    '.foxify-header',
    '[data-header-sticky]',
  ].join(',');
  const root = document.documentElement;
  const observedBanners = new Set();
  let animationFrame;

  const resizeObserver = new ResizeObserver(() => scheduleMeasurement());

  const isVisible = (element) => {
    const style = getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      !element.hidden &&
      element.getAttribute('aria-hidden') !== 'true' &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number.parseFloat(style.opacity || '1') > 0 &&
      rect.width > 0 &&
      rect.height > 0
    );
  };

  const setStyleProperty = (name, value) => {
    if (root.style.getPropertyValue(name) !== value) {
      root.style.setProperty(name, value);
    }
  };

  const syncObservedBanners = (banners) => {
    const currentBanners = new Set(banners);

    observedBanners.forEach((banner) => {
      if (!currentBanners.has(banner)) {
        resizeObserver.unobserve(banner);
        observedBanners.delete(banner);
      }
    });

    currentBanners.forEach((banner) => {
      if (!observedBanners.has(banner)) {
        resizeObserver.observe(banner);
        observedBanners.add(banner);
      }
    });
  };

  const hasFlexSpacer = (banner, bannerHeight) => {
    const possibleSpacers = document.querySelectorAll(
      '[class*="flex" i], [id*="flex" i], [data-fx-spacer], [data-flex-spacer]'
    );

    return [...possibleSpacers].some((element) => {
      if (
        element === banner ||
        element.contains(banner) ||
        banner.contains(element)
      ) {
        return false;
      }

      const marker = [...element.attributes]
        .map(({ name, value }) => `${name}=${value}`)
        .join(' ');
      const rect = element.getBoundingClientRect();
      const style = getComputedStyle(element);

      return (
        /flex.*(?:spacer|offset)|(?:spacer|offset).*flex/i.test(marker) &&
        style.display !== 'none' &&
        style.position !== 'fixed' &&
        style.position !== 'absolute' &&
        rect.height >= bannerHeight - 1
      );
    });
  };

  const hasExistingFlowSpace = (banner, bannerHeight, bodyPaddingTop) => {
    if (bodyPaddingTop >= bannerHeight - 1 || hasFlexSpacer(banner, bannerHeight)) {
      return true;
    }

    if (window.scrollY > 1) return false;

    const bannerBottom = banner.getBoundingClientRect().bottom;
    const firstVisibleHeader = [...document.querySelectorAll(headerSelector)].find(
      (header) => isVisible(header)
    );

    return (
      firstVisibleHeader &&
      firstVisibleHeader.getBoundingClientRect().top >= bannerBottom - 1
    );
  };

  const measure = () => {
    animationFrame = undefined;
    mutationObserver.disconnect();

    const banners = [...document.querySelectorAll(bannerSelector)];
    syncObservedBanners(banners);

    const body = document.body;
    if (!body) {
      observeMutations();
      return;
    }

    root.classList.remove('flex-announcement-overlay-active');
    body.removeAttribute('data-flex-announcement-needs-space');

    const baseBodyPaddingTop = getComputedStyle(body).paddingTop;
    const visibleBanners = banners.filter(isVisible);
    const positionedBanners = visibleBanners.filter((banner) =>
      ['fixed', 'sticky', 'absolute'].includes(getComputedStyle(banner).position)
    );
    const bannerHeight = Math.ceil(
      positionedBanners.reduce(
        (height, banner) => Math.max(height, banner.getBoundingClientRect().height),
        0
      )
    );

    setStyleProperty(
      '--flex-announcement-base-body-padding-top',
      baseBodyPaddingTop
    );
    setStyleProperty('--flex-announcement-height', `${bannerHeight}px`);

    if (!bannerHeight) {
      observeMutations();
      return;
    }

    const overlayBanner = positionedBanners.find((banner) =>
      ['fixed', 'absolute'].includes(getComputedStyle(banner).position)
    );
    const needsFlowSpace =
      overlayBanner &&
      !hasExistingFlowSpace(
        overlayBanner,
        bannerHeight,
        Number.parseFloat(baseBodyPaddingTop) || 0
      );

    root.classList.add('flex-announcement-overlay-active');
    if (needsFlowSpace) {
      body.setAttribute('data-flex-announcement-needs-space', 'true');
    }

    observeMutations();
  };

  function scheduleMeasurement() {
    if (animationFrame) return;
    animationFrame = requestAnimationFrame(measure);
  }

  const mutationObserver = new MutationObserver(() => scheduleMeasurement());
  const mutationObserverOptions = {
    attributes: true,
    attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
    childList: true,
    subtree: true,
  };

  function observeMutations() {
    mutationObserver.observe(document.documentElement, mutationObserverOptions);
  }

  const start = () => {
    observeMutations();
    window.addEventListener('resize', scheduleMeasurement, { passive: true });
    window.addEventListener('pageshow', scheduleMeasurement);
    document.addEventListener('shopify:section:load', scheduleMeasurement);
    document.addEventListener('shopify:section:unload', scheduleMeasurement);
    scheduleMeasurement();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
